'use client'

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from 'react'

export interface Skill {
  id: string | number
  name: string
  slug?: string | null
  description?: string | null
  icon?: string | null
  color?: string | null
  category?: string | null
  system_prompt?: string | null
  greeting?: string | null
  model?: string | null
  temperature?: number | null
  workers?: string[]
  navigation_config?: Record<string, any>
  conversation_starters?: string[]
  capabilities?: any
  is_active?: boolean
  is_template?: boolean
  status?: string
  is_trained?: number
}

export type SkillSelectionMode = 'auto' | 'forced' | 'off'

interface SkillsContextValue {
  skills: Skill[]
  loading: boolean
  refresh: () => Promise<void>
  selectionMode: SkillSelectionMode
  forcedSkillId: string | null
  activeSkill: Skill | null
  setMode: (mode: SkillSelectionMode) => void
  setForcedSkill: (id: string | number | null) => void
}

const SkillsContext = createContext<SkillsContextValue | null>(null)

const STORAGE_KEY = 'samantha.skill.selection'

type SkillsScope = 'selector' | 'config'

export function SkillsProvider({
  children,
  scope = 'selector',
}: {
  children: React.ReactNode
  scope?: SkillsScope
}) {
  const [skills, setSkills] = useState<Skill[]>([])
  const [loading, setLoading] = useState(true)
  const [selectionMode, setSelectionMode] = useState<SkillSelectionMode>('auto')
  const [forcedSkillId, setForcedSkillId] = useState<string | null>(null)

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY)
      if (!raw) return

      const parsed = JSON.parse(raw)

      if (parsed?.mode === 'auto' || parsed?.mode === 'forced' || parsed?.mode === 'off') {
        setSelectionMode(parsed.mode)
      }

      if (parsed?.forcedSkillId) {
        setForcedSkillId(String(parsed.forcedSkillId))
      }
    } catch {}
  }, [])

  const persist = (mode: SkillSelectionMode, id: string | null) => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ mode, forcedSkillId: id })
      )
    } catch {}
  }

  const refresh = useCallback(async () => {
    setLoading(true)

    try {
      const res = await fetch(`/api/ai/skills?scope=${scope}`, {
        cache: 'no-store',
        credentials: 'include',
      })

      const json = await res.json().catch(() => null)

      if (res.ok && json?.ok) {
        setSkills(Array.isArray(json.items) ? json.items : [])
      } else {
        setSkills([])
      }
    } catch (err) {
      console.warn('[SkillsContext] refresh error', err)
      setSkills([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const setMode = (mode: SkillSelectionMode) => {
    setSelectionMode(mode)

    if (mode !== 'forced') {
      setForcedSkillId(null)
      persist(mode, null)
      return
    }

    persist(mode, forcedSkillId)
  }

  const setForcedSkill = (id: string | number | null) => {
    const normalized = id == null ? null : String(id)

    setForcedSkillId(normalized)

    try {
        window.sessionStorage.removeItem('samantha:discovery')
        window.sessionStorage.removeItem('samantha:platform')
        window.sessionStorage.removeItem('samantha:style')
    } catch {}

    if (normalized) {
        setSelectionMode('forced')
        persist('forced', normalized)
    } else {
        setSelectionMode('auto')
        persist('auto', null)
    }
    }

  const activeSkill =
    selectionMode === 'forced' && forcedSkillId
      ? skills.find((skill) => String(skill.id) === String(forcedSkillId)) || null
      : null

  return (
    <SkillsContext.Provider
      value={{
        skills,
        loading,
        refresh,
        selectionMode,
        forcedSkillId,
        activeSkill,
        setMode,
        setForcedSkill,
      }}
    >
      {children}
    </SkillsContext.Provider>
  )
}

export function useSkills() {
  const ctx = useContext(SkillsContext)
  if (!ctx) throw new Error('useSkills must be used inside SkillsProvider')
  return ctx
}